import { locale, t, tn } from '@app/i18n';
import type { ApiData } from '@app/api/typed';
import { hopStatus, type AgentHop, type HopStatus, type LabPerson } from '@app/main/Conversar';
import { decisionIdentity, decisionTarget, type Decision as PdpDecision } from '@app/main/Decisions';
import { protocolLabel } from './ChatEntryModel';

/**
 * Vocabulário do chat «Ask» (04/09, `docs/contratos/chat-ask.md` §4).
 *
 * A pessoa pergunta em linguagem natural; o generalista escolhe a quem delegar e
 * que tool pedir; o especialista chama a tool no gateway. O que a tela mostra
 * sob cada resposta é a **trilha compacta** — Pessoa → Agente → «escolheu: …» →
 * Decisão — derivada de `hops` + `llm_hops` com o **mesmo** mapeamento de
 * `HopTrail` (`hopStatus`, importado de `Conversar`, nunca reimplementado).
 */

export type AgentAskResult = ApiData<'/api/v1/agents/{slug}/ask', 'post'>;
export type LlmHop = AgentAskResult['llm_hops'][number];
export type Refusal = AgentAskResult['refusals'][number];
export type AgentToolCall = AgentAskResult['agent_tool_calls'][number];
export type { LabPerson };

/** Os oito perfis do seed do laboratório (`ToolsPorPerfil` do Rego). */
export const PROFILES = [
  'analista',
  'analista-financeiro',
  'auditor',
  'cliente',
  'cobranca-svc',
  'noc-svc',
  'soc-svc',
  'suporte',
] as const;

/** O `detail` do salto `agente` quando o pod está sem chave (§2). */
export const LLM_DISABLED = 'llm_disabled';

// --- quem pergunta -----------------------------------------------------------

/** Um par livre chave/valor da aba Claims. */
export interface ClaimPair {
  key: string;
  value: string;
}

/** O que a aba Claims edita. */
export interface ClaimsForm {
  sub: string;
  profile: string | null;
  extras: ClaimPair[];
}

export const EMPTY_CLAIMS: ClaimsForm = { sub: '', profile: null, extras: [] };

/**
 * As claims que vão no pedido: `sub`, `perfil` (quando escolhido) e os pares
 * livres com chave preenchida. Um par sem chave não é claim — some em silêncio,
 * porque a tela já o mostra vazio e não há o que dizer sobre ele.
 *
 * `sub` e `perfil` ganham dos pares livres: escrever `sub` de novo como par
 * seria dizer duas coisas sobre a mesma claim, e a que a tela nomeia é a que vale.
 */
export function claimsFromForm(form: ClaimsForm): Record<string, unknown> {
  const claims: Record<string, unknown> = {};
  for (const pair of form.extras) {
    const key = pair.key.trim();
    if (key) claims[key] = pair.value;
  }
  claims.sub = form.sub.trim();
  if (form.profile) claims.perfil = form.profile;
  else delete claims.perfil;
  return claims;
}

/** Quem pergunta, resolvido: uma pessoa do elenco ou um conjunto de claims. */
export type Asker =
  | { kind: 'person'; person: LabPerson }
  | { kind: 'claims'; claims: Record<string, unknown> };

/** O `sub` de quem pergunta — o rótulo da bolha e a chave da trilha. */
export function askerSub(asker: Asker): string {
  return asker.kind === 'person' ? asker.person.sub : String(asker.claims.sub ?? '');
}

/** O perfil de quem pergunta, quando se sabe. */
export function askerProfile(asker: Asker): string | null {
  if (asker.kind === 'person') return asker.person.profile;
  const perfil = asker.claims.perfil;
  return typeof perfil === 'string' && perfil ? perfil : null;
}

/**
 * O que o cartão «token da pessoa» mostra: as claims **sem o JWT** — o pré-assinado
 * nunca sai do Secret, e o cunhado nunca volta do middleware. Do elenco vêm
 * `sub`, `perfil`, `aud` e `exp`; das claims, o que a pessoa escreveu.
 */
export function tokenClaims(asker: Asker): ClaimPair[] {
  if (asker.kind === 'person') {
    const { sub, profile, aud, exp } = asker.person;
    return [
      { key: 'sub', value: sub },
      { key: 'perfil', value: profile },
      { key: 'aud', value: aud },
      { key: 'exp', value: exp },
    ];
  }
  return Object.entries(asker.claims).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

/** Pronta para perguntar: pessoa escolhida, ou claims com `sub`. */
export function askerReady(asker: Asker | null): boolean {
  if (!asker) return false;
  return askerSub(asker).trim() !== '';
}

// --- o desfecho da pergunta ----------------------------------------------------

/**
 * O que aconteceu com a pergunta inteira.
 *
 * `answered` — o agente respondeu. `llm_disabled` — o pod está sem chave de
 * modelo: a rota devolve 200 com `answer: null` e o salto `agente` dizendo
 * `llm_disabled` (§2), e a tela explica em vez de mostrar uma bolha vazia.
 * `no_answer` — `answer` nulo por outro motivo, que a tela mostra pelo corpo.
 */
export type AskOutcome = 'answered' | 'llm_disabled' | 'no_answer';

export function askOutcome(result: AgentAskResult): AskOutcome {
  if (result.answer !== null && result.answer !== undefined) return 'answered';
  const agent = (result.hops ?? []).find((hop) => hop.step === 'agente');
  const body = (result.body ?? {}) as { error?: string; erro?: string };
  if (agent?.detail === LLM_DISABLED || body.error === LLM_DISABLED || body.erro === LLM_DISABLED) {
    return 'llm_disabled';
  }
  return 'no_answer';
}

/** A frase da bolha do agente quando não há `answer`. */
export function noAnswerPhrase(result: AgentAskResult): string {
  const body = (result.body ?? {}) as { error?: string; erro?: string; detail?: string; code?: string };
  const code = body.error ?? body.erro;
  // aim-5wc — the end of an AG-UI run: the sentence of the envelope code, with the agent's own code when it sent one.
  if (code === 'agent_run_error') {
    return body.code ? t('error.agent_run_error', { code: body.code }) : t('error.agent_run_error.noData');
  }
  if (code === 'agent_stream_incomplete') return t('error.agent_stream_incomplete');
  if (code) return t('chat.trail.noAnswerCode', { code });
  return t('chat.trail.noAnswerHttp', { status: result.http_status });
}

// --- a trilha compacta -------------------------------------------------------------

/** A decisão de autorização das escolhas do modelo, em uma palavra. */
export type Decision = 'permitido' | 'negado' | '—';

/**
 * A situação de uma escolha do modelo.
 *
 * Duas fontes, nesta ordem: o salto `a2a` correspondente, quando o backend o
 * anotou (ele deriva `situacao` do `http_status` **daquela** escolha, não da
 * pergunta); e o `http_status` da própria escolha quando não há salto — a mesma
 * regra de `callOutcome`: 2xx é `ok`, 401/403 é `negado` (o PDP decidiu),
 * o resto é `falhou`. `hopStatus` é o de `HopTrail`, importado: uma segunda
 * leitura de `situacao` aqui faria as duas telas discordarem sobre a mesma
 * chamada.
 */
export function llmHopStatus(hop: LlmHop, a2a: AgentHop | null): HopStatus {
  if (a2a?.situation) return hopStatus(a2a);
  if (hop.http_status >= 200 && hop.http_status <= 299) return 'ok';
  if (hop.http_status === 401 || hop.http_status === 403) return 'negado';
  return 'falhou';
}

/** Os saltos `a2a` da resposta, na ordem — um por item de `llm_hops`. */
function a2aHops(result: AgentAskResult): AgentHop[] {
  return (result.hops ?? []).filter((hop) => hop.step === 'a2a');
}

/**
 * A decisão da pergunta: `negado` se alguma escolha foi negada, `permitido` se
 * todas passaram, `—` quando o modelo não chamou nada (ou o caminho quebrou —
 * ninguém decidiu, e afirmar um veredito seria inventar).
 */
export function askDecision(result: AgentAskResult): Decision {
  const a2a = a2aHops(result);
  const statuses = (result.llm_hops ?? []).map((hop, i) => llmHopStatus(hop, a2a[i] ?? null));
  if (statuses.length === 0) return '—';
  if (statuses.includes('negado')) return 'negado';
  if (statuses.every((s) => s === 'ok')) return 'permitido';
  return '—';
}

/** Uma linha da trilha compacta. */
export interface AskTrailRow {
  id: string;
  kind: 'pessoa' | 'agente' | 'escolha' | 'decisao';
  label: string;
  text: string;
  status: HopStatus;
  /** Detalhe secundário (o `via → status` da escolha, o detalhe do executor). */
  secondary?: string;
}

const DECISION_STATUS: Record<Decision, HopStatus> = {
  permitido: 'ok',
  negado: 'negado',
  '—': null,
};

/** O veredito de uma escolha, como o resumo da Decisão o conta. */
export type HopVerdict = 'permitido' | 'negado' | 'indeterminado';

/** `ok` → permitido; `negado` → negado; `falhou` e «não informado» → indeterminado (ninguém decidiu). */
export function hopVerdict(status: HopStatus): HopVerdict {
  if (status === 'ok') return 'permitido';
  if (status === 'negado') return 'negado';
  return 'indeterminado';
}

/** O resumo do passo «Decisão». */
export interface DecisionSummary {
  /** O texto do chip: o veredito quando há uma escolha; a contagem («1 permitido · 1 negado») quando há várias. */
  text: string;
  /** O tom: o pior caso — negado > indeterminado > permitido; `null` = indeterminado ou sem escolha. */
  status: HopStatus;
  /** Uma linha por escolha («cobranca · faturas.consultar → negado»), para o tooltip. */
  lines: string[];
}

const VERDICT_ORDER: HopVerdict[] = ['permitido', 'negado', 'indeterminado'];

/**
 * Veredito → texto na tela.
 *
 * # O CUIDADO QUE ESTE PONTO EXIGE
 *
 * `HopVerdict` e `Decision` são valores de CÓDIGO (`'permitido' | 'negado' |
 * '—'`): o tipo os declara, a lógica os compara e os testes os afirmam. Até aqui
 * eles eram **também** o texto exibido — a mesma string servindo de identificador
 * e de frase. A extração separa as duas coisas e **só mexe na segunda**: a chave
 * do catálogo continua sendo o valor do tipo (`chat.verdict.permitido.*`), então
 * nada no código muda de nome.
 *
 * `verdictLabel` é a forma sem número («permitido»); `verdictCount` é a forma com
 * número, pelo plural do catálogo. Antes o plural era um índice `[n === 1 ? 0 :
 * 1]` — que acerta o inglês e erra o português no zero (a regra CLDR do
 * português manda 0 para `one`).
 */
const verdictLabel = (verdict: HopVerdict) => t(`chat.verdict.${verdict}.label`);
const verdictCount = (verdict: HopVerdict, n: number) => tn(`chat.verdict.${verdict}`, n);

/**
 * A Decisão resume TODAS as escolhas, não só a última (04/09): a conversa da
 * `ana` teve `minhafatura.consultar` permitido e `faturas.consultar` negado, e
 * a trilha dizia só «negado». Uma escolha → o veredito, como sempre; várias →
 * a contagem por veredito, com o tom do pior caso (`askDecision`) e uma linha
 * por escolha para o tooltip.
 */
export function decisionSummary(result: AgentAskResult): DecisionSummary {
  const a2a = a2aHops(result);
  const hops = result.llm_hops ?? [];
  const verdicts = hops.map((hop, i) => hopVerdict(llmHopStatus(hop, a2a[i] ?? null)));
  const lines = hops.map((hop, i) => `${hop.agent} · ${hop.tool} → ${verdicts[i]}`);
  const decision = askDecision(result);
  const status = DECISION_STATUS[decision];
  // `decision` pode ser `'—'`, que não é veredito e não tem rótulo: é o marcador
  // de «sem veredito» e sai como está.
  if (hops.length <= 1) {
    return { text: decision === '—' ? decision : verdictLabel(decision), status, lines };
  }

  const counts = VERDICT_ORDER.map((verdict) => [verdict, verdicts.filter((v) => v === verdict).length] as const);
  const text = counts
    .filter(([, n]) => n > 0)
    .map(([verdict, n]) => verdictCount(verdict, n))
    .join(' · ');
  return { text, status, lines };
}

/**
 * The step «Decisão» from the PDP decisions of the conversation (aim-6ph.9): one decision gives its verdict, several
 * give the count by verdict («3 permitidos · 1 negado»), with the tone of the worst case and one line per decision for
 * the tooltip. The PDP verdict and the trail verdict are the same vocabulary (`permitido | negado | indeterminado`).
 */
export function pdpDecisionSummary(decisions: PdpDecision[]): DecisionSummary {
  const verdicts = decisions.map((decision) => decision.verdict);
  const lines = decisions.map(
    (decision) => `${pdpRowText(decision)} → ${verdictLabel(decision.verdict)}`,
  );
  const status: HopStatus = verdicts.includes('negado')
    ? 'negado'
    : verdicts.every((verdict) => verdict === 'permitido')
      ? 'ok'
      : null;
  if (decisions.length === 1) return { text: verdictLabel(verdicts[0]!), status, lines };
  const text = VERDICT_ORDER.map((verdict) => [verdict, verdicts.filter((v) => v === verdict).length] as const)
    .filter(([, n]) => n > 0)
    .map(([verdict, n]) => verdictCount(verdict, n))
    .join(' · ');
  return { text, status, lines };
}

/**
 * «customer-service → noc» or «noc → network-mcp/check_outage»: who called and what, from the PDP decision. The
 * arrow is punctuation between two pieces of data, the same in every language, and it is composed here, and not in
 * the catalog.
 */
export function pdpRowText(decision: PdpDecision): string {
  return `${decisionIdentity(decision).texto} → ${decisionTarget(decision).texto}`;
}

/** «18,9» in pt-BR and «18.9» in en: the conversation time in seconds, with one decimal. */
export function conversationSeconds(durationMs: number): string {
  return new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
    durationMs / 1000,
  );
}

/**
 * Pessoa → Agente → «escolheu: cobranca · faturas.consultar» (uma por escolha)
 * → Decisão.
 *
 * As duas primeiras vêm dos saltos `pessoa` e `agente`, com a situação que o
 * backend anotou. Etapa não relatada fica «não informado» (`status: null`),
 * como em `HopTrail` — a diferença entre «o middleware não conta esse pedaço» e
 * «a chamada parou antes» importa para quem depura.
 */
export function askTrail(result: AgentAskResult, pdp: PdpDecision[] = []): AskTrailRow[] {
  const hops = result.hops ?? [];
  const person = hops.find((hop) => hop.step === 'pessoa') ?? null;
  const agent = hops.find((hop) => hop.step === 'agente') ?? null;
  const outcome = askOutcome(result);
  const a2a = a2aHops(result);

  const rows: AskTrailRow[] = [
    {
      id: 'pessoa',
      kind: 'pessoa',
      label: t('chat.trail.row.person'),
      text: result.person,
      status: person ? hopStatus(person) : null,
      secondary: person?.detail,
    },
    {
      id: 'agente',
      kind: 'agente',
      label: t('chat.trail.row.agent'),
      text:
        outcome === 'llm_disabled'
          ? t('chat.trail.withoutModelKey', { agent: result.agent })
          : agentText(result),
      status: agent ? hopStatus(agent) : null,
      secondary: outcome === 'llm_disabled' ? LLM_DISABLED : agent?.detail,
    },
  ];

  (result.llm_hops ?? []).forEach((hop, i) => {
    rows.push({
      id: `escolha-${i}`,
      kind: 'escolha',
      label: t('chat.trail.row.chose'),
      text: `${hop.agent} · ${hop.tool}`,
      status: llmHopStatus(hop, a2a[i] ?? null),
      secondary: `${hop.via} → ${hop.http_status}`,
    });
  });

  // An agent that did not report its choices has no «Decisão» here (aim-5wc §3.4, §3.5): the screen does not know
  // what it chose, and the verdict of each call is in Decisions (T16), behind the button under the trail.
  if (trailNotice(result) !== null) return rows;

  // aim-6ph.9 (T6 (a) of the mrolim): the PDP verdict when the decisions of the conversation came; before that, the
  // verdict that the agent reported, marked as such. The report of the agent is true data, and the mark says where it
  // comes from. A step without a verdict («—») says nothing about who reported it.
  const fromPdp = pdp.length > 0;
  const summary = fromPdp ? pdpDecisionSummary(pdp) : decisionSummary(result);
  rows.push({
    id: 'decisao',
    kind: 'decisao',
    label: t('chat.trail.row.decision'),
    text:
      fromPdp || summary.text === '—'
        ? summary.text
        : t('chat.trail.decision.fromAgent', { verdict: summary.text }),
    status: summary.status,
    // Com várias escolhas o tooltip lista cada uma com o seu veredito.
    secondary: summary.lines.length > 1 ? summary.lines.join('\n') : undefined,
  });
  return rows;
}

function agentText(result: AgentAskResult): string {
  const parts = [result.agent];
  // A configured entry says how the question went (§3.4): «sales-assistant · A2A 0.3». Data with punctuation.
  if (result.entry?.custom) parts.push(protocolLabel(result.entry.protocol));
  if (result.model) parts.push(result.model);
  if (result.rounds > 0) parts.push(tn('chat.trail.rounds', result.rounds));
  return parts.join(' · ');
}

/**
 * `denied_stop` (04/09): o modelo pediu outra ferramenta depois de uma recusa do
 * PDP e o agente NÃO executou. É o comportamento certo — a frase é positiva e a
 * marca é ✓, não ⊘.
 */
export const DENIED_STOP = 'denied_stop';

export type RefusalTone = 'denied' | 'positive';

export function refusalTone(refusal: Refusal): RefusalTone {
  return refusal.reason === DENIED_STOP ? 'positive' : 'denied';
}

/** «pediu fora do contrato: noc · incidentes.listar» (§4) — ou, em `denied_stop`, «não tentou contornar a recusa: …». */
export function refusalPhrase(refusal: Refusal): string {
  const params = { agent: refusal.agent, tool: refusal.tool };
  if (refusal.reason === DENIED_STOP) return t('chat.trail.refusalStopped', params);
  return t('chat.trail.refusalOutside', params);
}

// --- the trail without report (aim-5wc, prototype v2 §2.6, §3.4, §3.5) -------------------

/**
 * What the screen says below the trail when the agent did not report its choices in the Segura metadata:
 *
 * - `none` — an A2A agent without `hops`: it does not report its choices;
 * - `ag_ui` — an AG-UI agent: the tools it said it called, and the PDP verdict of each one is in Decisions;
 * - `null` — the Segura runtime reported the trail, as before the entry existed.
 *
 * It reads `trail_source`, never the absence of `llm_hops`: a Segura agent that chose nothing also has no `llm_hops`,
 * and it DID report — it reported that it chose nothing.
 */
export type TrailNotice = 'none' | 'ag_ui' | null;

export function trailNotice(result: AgentAskResult): TrailNotice {
  if (result.trail_source === 'none') return 'none';
  if (result.trail_source === 'ag_ui_events') return 'ag_ui';
  return null;
}

/** One line per tool an AG-UI agent named: «chamou get_order_status · respondeu». */
export function toolCallLines(result: AgentAskResult): { name: string; received: boolean }[] {
  return (result.agent_tool_calls ?? []).map((call) => ({ name: call.name, received: call.result_received }));
}
